import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment.development';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private conectadoSubject = new BehaviorSubject<boolean>(false);
  public conectado$ = this.conectadoSubject.asObservable();

  constructor() {}

  /**
   * Conectar al servidor Socket.io
   */
  conectar(token: string): void {
    if (this.socket?.connected) {
      console.log('✅ Socket ya está conectado');
      return;
    }

    // Si hay un socket previo desconectado, limpiar
    if (this.socket && !this.socket.connected) {
      console.log('🧹 Limpiando socket desconectado previo...');
      this.socket.removeAllListeners();
      this.socket = null;
    }

    console.log('🔌 Conectando a Socket.io...', environment.socketUrl);

    this.socket = io(environment.socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,              // Habilitar reconexión automática
      reconnectionAttempts: 5,         // Intentar 5 veces
      reconnectionDelay: 1000,         // Esperar 1 segundo entre intentos
      reconnectionDelayMax: 5000,      // Máximo 5 segundos de espera
      timeout: 20000                   // Timeout de 20 segundos
    });

    this.socket.on('connect', () => {
      console.log('✅ Conectado a Socket.io (ID:', this.socket?.id, ')');
      this.conectadoSubject.next(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Desconectado de Socket.io. Razón:', reason);
      this.conectadoSubject.next(false);
      
      // Si fue desconexión del servidor, intentar reconectar
      if (reason === 'io server disconnect') {
        console.log('🔄 Desconexión del servidor, reconectando...');
        this.socket?.connect();
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconectado después de', attemptNumber, 'intentos');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Intento de reconexión #', attemptNumber);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Falló la reconexión después de múltiples intentos');
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Error de conexión Socket.io:', error.message);
      this.conectadoSubject.next(false);
    });

    this.socket.on('error', (data) => {
      console.error('❌ Error del servidor Socket.io:', data);
    });
  }

  /**
   * Desconectar del servidor
   */
  desconectar(): void {
    if (this.socket) {
      console.log('🔌 Desconectando Socket.io...', new Error().stack);
      this.socket.disconnect();
      this.socket = null;
      this.conectadoSubject.next(false);
    } else {
      console.log('⚠️ Intento de desconectar pero socket ya es null');
    }
  }

  /**
   * Emitir un evento al servidor
   */
  emitir(evento: string, datos?: any): void {
    if (!this.socket || !this.socket.connected) {
      console.error('❌ Socket no está conectado');
      return;
    }
    console.log('📤 Emitiendo evento:', evento, datos);
    this.socket.emit(evento, datos);
  }

  /**
   * Escuchar un evento del servidor
   */
  escuchar<T>(evento: string): Observable<T> {
    return new Observable<T>(observer => {
      if (!this.socket) {
        console.error('❌ Socket no inicializado al intentar escuchar:', evento);
        observer.error('Socket no inicializado');
        return;
      }

      console.log('👂 Configurando listener para:', evento);
      
      this.socket.on(evento, (datos: T) => {
        console.log('📥 Evento recibido:', evento, datos);
        observer.next(datos);
      });

      // Cleanup cuando el observable se desuscribe
      return () => {
        if (this.socket) {
          console.log('🔇 Removiendo listener para:', evento);
          this.socket.off(evento);
        }
      };
    });
  }

  /**
   * Escuchar un evento una sola vez
   */
  escucharUnaVez<T>(evento: string): Observable<T> {
    return new Observable<T>(observer => {
      if (!this.socket) {
        observer.error('Socket no inicializado');
        return;
      }

      this.socket.once(evento, (datos: T) => {
        console.log('📥 Evento recibido (una vez):', evento, datos);
        observer.next(datos);
        observer.complete();
      });
    });
  }

  /**
   * Verificar si está conectado
   */
  estaConectado(): boolean {
    return this.socket?.connected || false;
  }
}

